import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    MapPin, Star, Wifi, Coffee, Users, Info, Bed, Check
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '@/components/SEO';

const AccommodationPage = () => {
    const accommodations = [
        // --- VICTORIA FALLS (ZIMBABWE) ---
        {
            name: 'The Victoria Falls Hotel',
            location: 'Victoria Falls, Zimbabwe',
            description: 'The historic "Grand Old Lady of the Falls" offering world-class service and direct access to the Victoria Falls Bridge.',
            rating: 5,
            amenities: ['Pool', 'Spa', 'Fine Dining', 'Free WiFi'],
            image: '/images/vic-falls-hotel.png',
            category: 'Luxury',
            place: 'Victoria Falls',
            price: '$450'
        },
        {
            name: 'Elephant Camp',
            location: 'Victoria Falls, Zimbabwe',
            description: 'Luxury tented camp situated on a private concession within the Victoria Falls National Park.',
            rating: 5,
            amenities: ['Private Plunge Pools', 'All Inclusive', 'Elephant Interaction'],
            image: '/images/elephant-camp-sunset.jpg',
            category: 'Luxury',
            place: 'Victoria Falls',
            price: '$800'
        },
        {
            name: 'Shearwater Explorers Village',
            location: 'Victoria Falls, Zimbabwe',
            description: 'Budget-friendly and stylish accommodation located in the heart of Victoria Falls town.',
            rating: 4,
            amenities: ['Pool', 'Bar', 'Restaurant', 'WiFi'],
            image: '/images/311229435.jpg',
            category: 'Budget',
            place: 'Victoria Falls',
            price: '$120'
        },
        {
            name: 'Royal Livingstone Hotel',
            location: 'Livingstone, Zambia',
            description: 'A luxurious 5-star hotel located on the banks of the Zambezi River with direct access to the Falls.',
            rating: 5,
            amenities: ['River Deck', 'Swimming Pool', 'Spa', 'Butler Service'],
            image: 'https://assets.anantara.com/image/upload/q_auto,f_auto,c_limit,w_1920/media/minor/anantara/images/royal-livingstone-by-anantara/gallery/overview/royal_livingstone_ext_lounge_grounds_dusk_1920x1037.jpg',
            category: 'Luxury',
            place: 'Livingstone',
            price: '$650'
        },
        {
            name: 'Avani Victoria Falls Resort',
            location: 'Livingstone, Zambia',
            description: 'Vibrant and colorful resort perfect for families, located right next to the Victoria Falls.',
            rating: 4,
            amenities: ['Large Pool', 'Family Friendly', 'Restaurants'],
            image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1000&q=80',
            category: 'Mid-Range',
            place: 'Livingstone',
            price: '$300'
        },
        {
            name: 'Hwange Safari Lodge',
            location: 'Hwange National Park',
            description: 'Located just outside the main park entrance, famous for its waterhole teeming with elephants.',
            rating: 4,
            amenities: ['Waterhole View', 'Pool', 'Conferencing', 'Game Drives'],
            image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80',
            category: 'Mid-Range',
            place: 'Hwange',
            price: '$250'
        },
        {
            name: 'Somalisa Camp',
            location: 'Hwange National Park',
            description: 'Famous for its elephant pool where giants drink the same water as you.',
            rating: 5,
            amenities: ['Tented Suites', 'Pool', 'Game Viewing', 'All Inclusive'],
            image: 'https://africanbushcamps.com/wp-content/uploads/2018/05/Somalisa_450-1024x683.jpg',
            category: 'Luxury',
            place: 'Hwange',
            price: '$950'
        },
        {
            name: 'Chobe Safari Lodge',
            location: 'Kasane, Botswana',
            description: 'On the banks of the Chobe River, offering the best boat cruises and game drives into Chobe.',
            rating: 4,
            amenities: ['River View', 'Boat Cruises', 'Buffet Dining'],
            image: 'https://www.chobesafarilodge.com/images/slideshow/chobe-safari-lodge-pool-2025-2.jpg',
            category: 'Mid-Range',
            place: 'Kasane',
            price: '$280'
        },
        {
            name: 'Chobe Game Lodge',
            location: 'Chobe National Park',
            description: 'The only permanent game lodge situated within the famous Chobe National Park.',
            rating: 5,
            amenities: ['Riverside', 'Eco-friendly', 'Game Drives'],
            image: '/images/Chobe-Game-Lodge-Swimming-Pool-and-Restaurant.jpg',
            category: 'Luxury',
            place: 'Kasane',
            price: '$600'
        },
        {
            name: 'Muchenje Safari Lodge',
            location: 'Chobe National Park, Botswana',
            description: 'Owner-run and situated on the quieter western side of Chobe, offering stunning panoramic views and a comprehensive all-inclusive safari experience.',
            rating: 5,
            amenities: ['Panoramic Views', 'Night Drives', 'Mokoro Trips', 'All Inclusive'],
            image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Kasane',
            price: '$585'
        },
        {
            name: 'Tlouwana Camp',
            location: 'Chobe National Park, Botswana',
            description: 'A classic tented safari camp offering authentic wildlife encounters in the Chobe National Park. Experience the magic of the African bush in fully serviced comfort.',
            rating: 5,
            amenities: ['Classic Tented Safari', 'Morning Boat Cruises', 'Game Drives', 'Village Tours'],
            image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Kasane',
            price: '$472'
        },
        {
            name: 'Khwai Expeditions Camp',
            location: 'Khwai Concession, Botswana',
            description: 'Luxury tented camp overlooking a seasonal lagoon, offering an authentic and intimate safari experience.',
            rating: 5,
            amenities: ['Private Decks', 'Gourmet Dining', 'Lagoon View', 'Eco-Friendly'],
            image: '/images/khwai-camp.png',
            category: 'Luxury',
            place: 'Kasane',
            price: '$455'
        },
        {
            name: 'Nata Lodge',
            location: 'Nata, Botswana',
            description: 'A beautiful oasis set among mokolwane palms on the edge of the Makgadikgadi Salt Pans.',
            rating: 4,
            amenities: ['Pool', 'Restaurant', 'Gift Shop', 'Salt Pan Tours'],
            image: '/images/nata-lodge.png',
            category: 'Mid-Range',
            place: 'Kasane',
            price: '$150'
        },
        {
            name: 'Setari Camp',
            location: 'Okavango Delta, Botswana',
            description: 'An intimate water-based safari camp offering a true island experience with elevated wooden walkways and stunning delta views.',
            rating: 5,
            amenities: ['Delta Transfers', 'Safari Activities', 'Village Trip', 'All Inclusive'],
            image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Okavango Delta',
            price: '$480'
        },
        {
            name: 'Kala Camp',
            location: 'Okavango Delta, Botswana',
            description: 'A sophisticated luxury lodge where contemporary design meets the wild beauty of the Delta, featuring expert-led wildlife encounters.',
            rating: 5,
            amenities: ['Airstrip Transfers', 'Gourmet Dining', 'Game Drives', 'Laundry Service'],
            image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Okavango Delta',
            price: '$480'
        },
        {
            name: 'Kala Treehouse',
            location: 'Okavango Delta, Botswana',
            description: 'The ultimate exclusive retreat. A private treehouse suite offering total seclusion, dedicated staff, and private helicopter transfers.',
            rating: 5,
            amenities: ['Exclusive Use', 'Private Guide', 'Helicopter Transfers', 'Chef Service'],
            image: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Okavango Delta',
            price: '$7,000'
        },
        {
            name: 'Angama Mara',
            location: 'Maasai Mara, Kenya',
            description: 'Suspended high above the floor of Africa’s Great Rift Valley, overlooking the Mara.',
            rating: 5,
            amenities: ['Panoramic Views', 'Luxury Suites', 'Game Drives'],
            image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Maasai Mara',
            price: '$1200'
        },
        {
            name: 'Keekorok Lodge',
            location: 'Maasai Mara, Kenya',
            description: 'The first lodge built in the Mara Game Reserve, offering traditional safari hospitality.',
            rating: 4,
            amenities: ['Hippo Bar', 'Pool', 'Balloon Safaris'],
            image: '/images/a-57.jpg',
            category: 'Mid-Range',
            place: 'Maasai Mara',
            price: '$350'
        },
        {
            name: 'Royal Mara Safari Lodge',
            location: 'Maasai Mara, Kenya',
            description: 'Luxury tented safari lodge situated along the Mara River, offering premier game viewing and elegant bush hospitality in the heart of the Maasai Mara.',
            rating: 5,
            amenities: ['River View', 'Luxury Tents', 'Game Drives', 'Bush Dining'],
            image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Maasai Mara',
            price: '$684'
        },
        {
            name: 'Mara Intrepids Camp',
            location: 'Maasai Mara, Kenya',
            description: 'Overlooking the Talek River, this camp offers a classic safari experience with luxury tents and world-class game viewing.',
            rating: 5,
            amenities: ['3 Game Drives', 'Airstrip Transfers', 'All Inclusive', 'River View'],
            image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Maasai Mara',
            price: '$550'
        },
        {
            name: 'Mara Elatia',
            location: 'Maasai Mara, Kenya',
            description: 'An intimate and exclusive camp in the Maasai Mara, offering personalized service and proximity to the Great Migration routes.',
            rating: 5,
            amenities: ['Intimate Camp', 'Game Drives', 'Bush Breakfast', 'Airstrip Transfers'],
            image: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Maasai Mara',
            price: '$550'
        },
        {
            name: 'Samburu Intrepids',
            location: 'Samburu, Kenya',
            description: 'A luxury oasis on the banks of the Ewaso Nyiro River, offering unique wildlife viewing in the rugged Samburu landscape.',
            rating: 5,
            amenities: ['River Front', 'Swimming Pool', 'Guided Walks', 'Cultural Tours'],
            image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Samburu',
            price: '$495'
        },
        {
            name: 'Great Rift Valley Lodge',
            location: 'Great Rift Valley, Kenya',
            description: 'Perched on the Eburu Escarpment with stunning views of Lake Naivasha and the Rift Valley, featuring an award-winning golf course.',
            rating: 5,
            amenities: ['Golf Course', 'Lake Views', 'Swimming Pool', 'Tennis Courts'],
            image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1000&q=80',
            category: 'Luxury',
            place: 'Great Rift Valley',
            price: '$365'
        },
        {
            name: 'Kizingo Eco Lodge',
            location: 'Lamu, Kenya',
            description: 'An award-winning eco-lodge on the secluded dunes of Lamu Island, offering a peaceful and sustainable beach escape.',
            rating: 4,
            amenities: ['Beach Front', 'Eco-Friendly', 'Secluded', 'Water Sports'],
            image: 'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?auto=format&fit=crop&w=1000&q=80',
            category: 'Mid-Range',
            place: 'Lamu',
            price: '$315'
        },
        {
            name: 'Voyager Ziwani Camp',
            location: 'Tsavo West, Kenya',
            description: 'Situated on the edge of Tsavo West National Park, this camp offers stunning views of Mount Kilimanjaro and authentic safari adventures.',
            rating: 4,
            amenities: ['Nature Walks', 'Night Game Drives', 'Kilimanjaro Views', 'Full Board'],
            image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1000&q=80',
            category: 'Mid-Range',
            place: 'Tsavo West',
            price: '$175'
        },
        {
            name: 'Voyager Beach Resort',
            location: 'Mombasa, Kenya',
            description: 'A vibrant nautical-themed resort located on Nyali Beach, offering extensive family facilities and a variety of water sports.',
            rating: 4,
            amenities: ['All Inclusive', 'Water Sports', 'Kids Club', 'Multiple Pools'],
            image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1000&q=80',
            category: 'Mid-Range',
            place: 'Mombasa',
            price: '$160'
        }
    ];

    const categories = ['All', 'Luxury', 'Mid-Range', 'Budget'];
    const places = ['All', 'Victoria Falls', 'Livingstone', 'Hwange', 'Kasane', 'Okavango Delta', 'Maasai Mara', 'Samburu', 'Great Rift Valley', 'Lamu', 'Tsavo West', 'Mombasa'];

    const [searchParams] = useSearchParams();
    const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'All');
    const [activePlace, setActivePlace] = useState(searchParams.get('place') || 'All');

    useEffect(() => {
        const placeParam = searchParams.get('place');
        const categoryParam = searchParams.get('category');
        if (placeParam) setActivePlace(placeParam);
        if (categoryParam) setActiveCategory(categoryParam);
    }, [searchParams]);

    return (
        <div className="min-h-screen">
            <SEO
                title="Accommodation"
                description="Find the best places to stay in Victoria Falls, Botswana, and Kenya. Luxury lodges, safari camps, and hotels."
                keywords="Victoria Falls Hotels, Safari Lodges, Botswana Camps, Luxury Accommodation Africa, Elephant Camp, Victoria Falls Hotel"
                canonicalUrl="https://www.peregrinetoursandsafaris.com/accommodation"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": "African Safari Lodges and Hotels",
                    "description": "Selected luxury and mid-range accommodations in Southern and Eastern Africa.",
                    "itemListElement": accommodations.map((hotel, index) => ({
                        "@type": "ListItem",
                        "position": index + 1,
                        "item": {
                            "@type": "LodgingBusiness",
                            "name": hotel.name,
                            "description": hotel.description,
                            "image": hotel.image.startsWith('/')
                                ? `https://www.peregrinetoursandsafaris.com${hotel.image}`
                                : hotel.image,
                            "address": {
                                "@type": "PostalAddress",
                                "addressLocality": hotel.location.split(',')[0].trim(),
                                "addressCountry": hotel.location.split(',')[1]?.trim() || "Africa"
                            },
                            "starRating": {
                                "@type": "Rating",
                                "ratingValue": hotel.rating
                            },
                            "amenityFeature": hotel.amenities.map(amenity => ({
                                "@type": "LocationFeatureSpecification",
                                "name": amenity,
                                "value": true
                            }))
                        }
                    }))
                }}
            />

            <Navbar />

            <section className="relative pt-32 pb-16 bg-gradient-to-b from-muted/30 to-background">
                <div className="container-custom text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-serif">
                        Luxury Accommodation
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Find the perfect place to stay in Southern and East Africa's most iconic destinations.
                    </p>
                </div>
            </section>

            {/* Place & Category Filters */}
            <section className="py-8 bg-background border-y border-border">
                <div className="container-custom space-y-6">
                    <div className="flex flex-wrap justify-center gap-3">
                        <span className="w-full text-center text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Location</span>
                        {places.map((place) => (
                            <button
                                key={place}
                                onClick={() => setActivePlace(place)}
                                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activePlace === place
                                    ? 'bg-primary text-primary-foreground shadow-lg'
                                    : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
                                    }`}
                            >
                                {place}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                        <span className="w-full text-center text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Category</span>
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${activeCategory === category
                                    ? 'bg-accent text-accent-foreground'
                                    : 'bg-muted text-muted-foreground hover:bg-accent/10 hover:text-accent'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Accommodation Grid */}
            <section className="section-padding bg-background">
                <div className="container-custom space-y-16">
                    {(activePlace === 'All' ? places.filter(p => p !== 'All') : [activePlace]).map((place) => {
                        const placeAccommodations = accommodations.filter(item =>
                            item.place === place &&
                            (activeCategory === 'All' || item.category === activeCategory)
                        );

                        if (placeAccommodations.length === 0) return null;

                        return (
                            <div key={place} className="space-y-6">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-2xl font-bold font-serif text-foreground">{place}</h2>
                                    <div className="h-px bg-border flex-1" />
                                </div>

                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {placeAccommodations.map((hotel, index) => (
                                        <Card key={index} className="group overflow-hidden border-2 hover:border-primary/30 transition-all duration-300 flex flex-col">
                                            <div className="relative h-64 overflow-hidden">
                                                <img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                                <div className="absolute top-4 right-4 bg-background/95 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1 shadow-lg">
                                                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                                    <span className="text-xs font-bold">{hotel.rating}</span>
                                                </div>
                                                <div className="absolute top-4 left-4 bg-primary/90 text-primary-foreground backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2">
                                                    <MapPin className="w-3 h-3" />
                                                    <span className="text-[10px] font-bold uppercase tracking-wider">{hotel.place}</span>
                                                </div>
                                            </div>

                                            <div className="p-6 flex-1 flex flex-col">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h3 className="text-xl font-bold text-foreground font-serif line-clamp-1">{hotel.name}</h3>
                                                </div>
                                                <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-2">{hotel.description}</p>

                                                <div className="flex flex-wrap gap-2 mb-6">
                                                    {hotel.amenities.slice(0, 3).map((amenity, i) => (
                                                        <span key={i} className="text-[10px] uppercase font-bold px-2 py-1 bg-muted rounded text-muted-foreground">
                                                            {amenity}
                                                        </span>
                                                    ))}
                                                </div>

                                                <div className="flex items-center justify-between py-4 border-t border-border mt-auto">
                                                    <div className="flex flex-col">
                                                        <span className="text-lg font-black text-primary">From {hotel.price}</span>
                                                        <span className="text-[9px] text-muted-foreground uppercase">Per Night</span>
                                                    </div>
                                                    <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground px-6 shadow-md hover:shadow-lg transition-all">
                                                        <Link to="/booking" state={{ bookItem: hotel.name }}>
                                                            Book Now
                                                        </Link>
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AccommodationPage;
